import { createRoute, Link } from '@tanstack/react-router';
import { FolderX } from 'lucide-react';
import { useCallback } from 'react';

import { EmptyState, ErrorState, SkeletonGrid } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useProjectQuery } from '@/features/projects/projects-queries';
import { PrototypesBrowser } from '@/features/prototypes/prototypes-browser';
import {
  validatePrototypesSearch,
  type PrototypesSearch,
} from '@/features/prototypes/prototypes-search';

import { rootRoute } from './root.route';

/**
 * A project and its prototypes (spec R6.6).
 *
 * The route asks for two things and they answer different questions. The
 * *project* read is what proves the id in the URL means something: it 404s for
 * an unknown project, and this page turns that into a clean "does not exist"
 * rather than an error. The *prototype list* cannot do that job — it returns an
 * empty page for an unknown `projectId`, which is indistinguishable from a real
 * project nobody has uploaded to yet, and rendering "no prototypes yet" for a
 * deleted project would be a quiet lie.
 *
 * So the header waits for the project, and the list is only mounted once the
 * project is known to exist.
 */
const ProjectDetailPage = () => {
  const { projectId } = projectDetailRoute.useParams();
  const search = projectDetailRoute.useSearch();
  const navigate = projectDetailRoute.useNavigate();
  const project = useProjectQuery(projectId);

  const handleSearchChange = useCallback(
    (next: PrototypesSearch, replace = false) => {
      void navigate({
        to: '/projects/$projectId',
        params: { projectId },
        search: () => next,
        replace,
      });
    },
    [navigate, projectId],
  );

  if (project.isPending) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <SkeletonGrid label="Loading prototypes" />
      </div>
    );
  }

  if (project.isError) {
    if (project.error.status === 404) {
      return (
        <EmptyState
          icon={FolderX}
          title="This project does not exist"
          description="It may have been deleted, or the link may be wrong. Deleting a project takes its prototypes, versions and comment threads with it."
          action={
            <Button asChild variant="outline">
              <Link to="/">Back to projects</Link>
            </Button>
          }
        />
      );
    }

    return (
      <ErrorState
        title="Could not load this project"
        error={project.error}
        onRetry={() => {
          void project.refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {project.data.name}
        </h1>
        <p className="max-w-prose text-sm text-muted-foreground">
          {project.data.description ??
            'No description. Every prototype in this project is listed below.'}
        </p>
      </div>

      <PrototypesBrowser
        projectId={projectId}
        search={search}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
};

export const projectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId',
  validateSearch: validatePrototypesSearch,
  component: ProjectDetailPage,
});
